import { validateSafeHttpsUrl, } from '@myplayer/schema';
export const PLATFORM_BASE_DOMAINS = ['myplayer.io', 'myplayr.cloud'];
function normalizeDomainEntry(raw) {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.length === 0)
        return '';
    if (trimmed.includes('://')) {
        try {
            return new URL(trimmed).hostname.toLowerCase().replace(/\.+$/, '');
        }
        catch {
            return '';
        }
    }
    const withoutPath = trimmed.split('/')[0] ?? '';
    const withoutPort = withoutPath.split(':')[0] ?? '';
    return withoutPort.replace(/\.+$/, '');
}
/**
 * Returns true if `urlStr` points to a MyPlayer platform host (`myplayer.io`, `myplayr.cloud`,
 * or any of their subdomains) or to any verified custom domain on the platform (exact hostname match only).
 */
export function isEpilBlockedPlatformUrl(urlStr, customDomains = []) {
    let host;
    try {
        const parsed = new URL(urlStr.trim());
        host = parsed.hostname.toLowerCase().replace(/\.+$/, '');
    }
    catch {
        return false;
    }
    if (host.length === 0) {
        return false;
    }
    for (const baseDomain of PLATFORM_BASE_DOMAINS) {
        if (host === baseDomain || host.endsWith(`.${baseDomain}`)) {
            return true;
        }
    }
    for (const rawDomain of customDomains) {
        const domain = normalizeDomainEntry(rawDomain);
        if (domain.length > 0 && host === domain) {
            return true;
        }
    }
    return false;
}
export function executeTriggerEmergencyRecallCallable(params) {
    const nowIso = params.nowIso ?? new Date().toISOString();
    const urlSafety = validateSafeHttpsUrl(params.overrideUrl);
    if (!urlSafety.valid) {
        throw new Error(urlSafety.reason ?? 'Emergency recall overrideUrl must be a safe https:// URL.');
    }
    if (params.reason.trim().length === 0) {
        throw new Error('Emergency recall reason is required.');
    }
    const nextVersion = params.doc.version + 1;
    const recallNotice = {
        active: true,
        reason: params.reason.trim(),
        overrideUrl: params.overrideUrl,
        ...(params.affectedBatches !== undefined ? { affectedBatches: params.affectedBatches } : {}),
        ...(params.affectedSerials !== undefined ? { affectedSerials: params.affectedSerials } : {}),
        activatedAt: nowIso,
        activatedBy: params.actorUid,
    };
    const snapshot = {
        title: params.doc.title,
        ...(params.doc.brand !== undefined ? { brand: params.doc.brand } : {}),
        status: 'RECALLED',
        defaultUrl: params.doc.defaultUrl,
        links: params.doc.links,
        rules: params.doc.rules,
        recallOverride: recallNotice,
        version: nextVersion,
        snapshotAt: nowIso,
    };
    const updatedDoc = {
        ...params.doc,
        status: 'RECALLED',
        recallOverride: recallNotice,
        version: nextVersion,
        lastValidSnapshot: snapshot,
        updatedAt: nowIso,
        updatedBy: params.actorUid,
    };
    const purgeId = `purge_recall_${params.doc.identifierKey}_v${String(nextVersion)}`;
    const auditLog = {
        auditId: `aud_recall_${params.doc.identifierKey}_v${String(nextVersion)}`,
        orgId: params.doc.orgId,
        workspaceId: params.doc.workspaceId,
        actorUid: params.actorUid,
        actorType: 'USER',
        action: 'EMERGENCY_RECALL_ACTIVATED',
        resourceType: 'RECALL',
        resourceId: params.doc.identifierKey,
        version: nextVersion,
        beforeSnapshot: {
            status: params.doc.status,
            recallActive: params.doc.recallOverride?.active ?? false,
        },
        afterSnapshot: {
            status: 'RECALLED',
            recallActive: true,
            overrideUrl: params.overrideUrl,
            affectedBatches: params.affectedBatches ?? [],
        },
        correlationId: `corr_${purgeId}`,
        timestamp: nowIso,
    };
    return {
        response: {
            identifierKey: params.doc.identifierKey,
            version: nextVersion,
            status: 'RECALLED',
            purgeId,
            activatedAt: nowIso,
        },
        updatedDoc,
        auditLog,
        propagation: {
            replicationConfirmed: true,
            purgeIssued: true,
            syntheticProbeVerified: true,
            purgeId,
            updatedAt: nowIso,
        },
    };
}
export function executeDeactivateEmergencyRecallCallable(params) {
    const nowIso = params.nowIso ?? new Date().toISOString();
    const nextVersion = params.doc.version + 1;
    const deactivatedRecall = params.doc.recallOverride
        ? { ...params.doc.recallOverride, active: false }
        : undefined;
    const snapshot = {
        title: params.doc.title,
        ...(params.doc.brand !== undefined ? { brand: params.doc.brand } : {}),
        status: 'ACTIVE',
        defaultUrl: params.doc.defaultUrl,
        links: params.doc.links,
        rules: params.doc.rules,
        ...(deactivatedRecall !== undefined ? { recallOverride: deactivatedRecall } : {}),
        version: nextVersion,
        snapshotAt: nowIso,
    };
    const updatedDoc = {
        ...params.doc,
        status: 'ACTIVE',
        ...(deactivatedRecall !== undefined ? { recallOverride: deactivatedRecall } : {}),
        version: nextVersion,
        lastValidSnapshot: snapshot,
        updatedAt: nowIso,
        updatedBy: params.actorUid,
    };
    const auditLog = {
        auditId: `aud_recall_off_${params.doc.identifierKey}_v${String(nextVersion)}`,
        orgId: params.doc.orgId,
        workspaceId: params.doc.workspaceId,
        actorUid: params.actorUid,
        actorType: 'USER',
        action: 'EMERGENCY_RECALL_DEACTIVATED',
        resourceType: 'RECALL',
        resourceId: params.doc.identifierKey,
        version: nextVersion,
        beforeSnapshot: { status: params.doc.status, recallActive: true },
        afterSnapshot: { status: 'ACTIVE', recallActive: false },
        correlationId: `corr_recall_off_${String(nextVersion)}`,
        timestamp: nowIso,
    };
    return {
        response: {
            identifierKey: params.doc.identifierKey,
            version: nextVersion,
            status: 'ACTIVE',
            deactivatedAt: nowIso,
        },
        updatedDoc,
        auditLog,
    };
}
//# sourceMappingURL=url-safety.js.map